import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaOHVRegistrationPage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">OHV Registration</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada Off-Highway Vehicle (OHV) Registration</h1>
        <p className="text-obsidian-300">Registration requirements for ATVs, dirt bikes, and other off-highway vehicles in Nevada.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Which OHVs Must Be Registered?</h2>
          <p>Nearly all off-highway vehicles (OHVs) that are <strong>1976 or newer</strong> and have an engine displacement <strong>greater than 70cc</strong> must be registered with the Nevada DMV and display a registration decal.</p>
          <p className="mt-3">This includes ATVs, dirt bikes, sand rails, dune buggies, and similar vehicles not intended for highway use.</p>
        </section>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <p className="text-amber-800 font-bold text-sm flex items-center space-x-2"><i className="ri-information-line"></i><span>Important: Mail-Only Registration</span></p>
          <p className="text-amber-700 text-xs mt-1">OHV registrations are handled <strong>by mail only</strong> or through a Nevada OHV dealer. DMV field offices do NOT process OHV registrations in person.</p>
        </div>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">OHV Registration Fees</h2>
          <div className="bg-obsidian-50 rounded-xl p-6">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-obsidian-200"><th className="text-left py-2 font-semibold text-obsidian-900">Registration Option</th><th className="text-right py-2 font-semibold text-obsidian-900">Fee</th></tr></thead>
              <tbody className="divide-y divide-obsidian-100">
                {[['1-Year Decal', '$20.00'], ['3-Year Decal', '$60.00'], ['Certificate of Title (Nevada residents)', '$28.25'], ['Duplicate Decal', 'Varies']].map(([l, a]) => (
                  <tr key={l}><td className="py-2.5 text-obsidian-700">{l}</td><td className="py-2.5 text-right font-medium text-obsidian-900">{a}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Required Documents</h2>
          <ul className="space-y-3">
            {[
              'Completed OHV Registration Application (Form OHV 001)',
              'Proof of ownership (title, bill of sale, or manufacturer\'s certificate of origin)',
              'Payment for registration fees',
              'Mailing address for decal delivery',
            ].map(item => (
              <li key={item} className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-checkbox-circle-line text-green-500"></i></div>
                <span className="text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">OHV Titling</h2>
          <p className="text-sm">A certificate of title is required to substantiate legal ownership of an OHV. The title fee is $28.25 for Nevada residents. Titling is separate from registration and is also handled by mail.</p>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Questions About OHV Registration?</h3>
          <p className="text-obsidian-600 text-sm mb-4">Contact Speedy DMV Services for guidance on OHV registration requirements in Nevada.</p>
          <Link to="/contact" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Contact Us</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaOHVRegistrationPage;
